import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Paper } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { Labelbox, Table, Modal, ContractInvoiceView } from "../../components";
import EfilingSignedConsentsDisplay from "../../components/EfilingSignedConsentsDisplay";
import EfilingDocumentPreviewModal from "../../components/EfilingDocumentPreviewModal";
import moment from "moment";
import "./efilingAdmin.scss";
import {
  fetchAllClientEfilings,
  updateClientEfiling,
} from "../../utils/efilingApi";
import { Toaster } from "../../utils/common";
import {
  canAccessEfilingPage,
  canEditEfilingRecord,
  canEditEfilingStatus,
  getLoggedInUserId,
} from "../../utils/efilingAccess";
import {
  recordToEditForm,
  validateEditEfilingForm,
  validateEfilingStatusForm,
  buildEfilingUpdatePayload,
  buildEfilingStatusUpdatePayload,
} from "../../utils/efilingFormUtils";
import {
  recordToBusinessEditForm,
  validateBusinessEfilingForm,
  buildBusinessEfilingPayload,
} from "../../utils/businessEfilingFormUtils";
import EfilingEditModal from "../../components/EfilingEditModal";
import EfilingStatusEditModal from "../../components/EfilingStatusEditModal";
import { INVOICE_ACTION, DOCUMENT_ACTION } from "../../redux/action";
import { getClientInvoiceId } from "../../utils/invoiceIds";
import API from "../../utils/api";
import { useDispatch } from "react-redux";
import {
  buildBusinessOfficeInvoiceDropdown,
  findBusinessOfficeDocument,
  getOfficeDocumentLink,
  getOfficeDocumentName,
} from "../../utils/businessEfilingOfficeDocuments";

const STATUS_TABS = [
  { id: "All", label: "All" },
  { id: "Pending", label: "Pending" },
  { id: "Efiled", label: "Efiled" },
  { id: "Rejected", label: "Rejected" },
];

function renderEfilingStatusBadge(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const slug = value.toLowerCase().replace(/\s+/g, "");
  return <span className={`status-badge status-${slug}`}>{value}</span>;
}

const DATE_RANGES = [
  "All",
  "Today",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "This Quarter",
  "Last Quarter",
  "This Year",
  "Last Year",
].map((x) => ({ id: x, value: x, keepAtTop: x === "All" }));

function formatMoneyDisplay(num) {
  const n = Number(num);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function yesNoDisplay(val) {
  if (val === "Yes" || val === "No") return val;
  if (val === 1 || val === "1" || val === true || val === "true") return "Yes";
  if (val === 0 || val === "0" || val === false || val === "false") return "No";
  const s = String(val ?? "")
    .trim()
    .toLowerCase();
  if (!s) return "";
  if (["yes", "y"].includes(s)) return "Yes";
  if (["no", "n"].includes(s)) return "No";
  return String(val);
}

function formatDateNoTz(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDateRange(range) {
  if (!range || range === "All") return { start_date: null, end_date: null };
  const today = new Date();
  let startDate, endDate;

  switch (range) {
    case "Today":
      startDate = new Date(today);
      endDate = new Date(today);
      break;
    case "This Week":
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    case "Last Week":
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay() - 7);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    case "This Month":
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case "Last Month":
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case "This Quarter": {
      const thisQuarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
      startDate = new Date(today.getFullYear(), thisQuarterStartMonth, 1);
      endDate = new Date(today.getFullYear(), thisQuarterStartMonth + 3, 0);
      break;
    }
    case "Last Quarter": {
      let lastQuarterStartMonth = Math.floor(today.getMonth() / 3) * 3 - 3;
      let lastQuarterYear = today.getFullYear();
      if (lastQuarterStartMonth < 0) {
        lastQuarterStartMonth += 12;
        lastQuarterYear -= 1;
      }
      startDate = new Date(lastQuarterYear, lastQuarterStartMonth, 1);
      endDate = new Date(lastQuarterYear, lastQuarterStartMonth + 3, 0);
      break;
    }
    case "This Year":
      startDate = new Date(today.getFullYear(), 0, 1);
      endDate = new Date(today.getFullYear(), 11, 31);
      break;
    case "Last Year":
      startDate = new Date(today.getFullYear() - 1, 0, 1);
      endDate = new Date(today.getFullYear() - 1, 11, 31);
      break;
    default:
      return { start_date: null, end_date: null };
  }

  return {
    start_date: formatDateNoTz(startDate),
    end_date: formatDateNoTz(endDate),
  };
}

export default function EfilingAdmin() {
  const navigate = useNavigate();
  const [categoryTab, setCategoryTab] = useState("individual");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [dateRange, setDateRange] = useState("This Year");
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(recordToEditForm({}));
  const [editFormErrors, setEditFormErrors] = useState({});
  const [statusEditOpen, setStatusEditOpen] = useState(false);
  const [statusEditSaving, setStatusEditSaving] = useState(false);
  const [statusEditRow, setStatusEditRow] = useState(null);
  const [statusEditForm, setStatusEditForm] = useState({
    filing_status: "Pending",
    other_comments: "",
  });
  const [statusEditFormErrors, setStatusEditFormErrors] = useState({});
  const [invoicePreview, setInvoicePreview] = useState({
    open: false,
    data: null,
  });
  const [officeDocuments, setOfficeDocuments] = useState([]);
  const [officeDocPreview, setOfficeDocPreview] = useState({
    open: false,
    url: "",
    documentName: "",
    clientDocumentId: "",
  });
  const dispatch = useDispatch();
  const canEditStatus = canEditEfilingStatus();
  const showEditAction = canEditEfilingRecord();
  const loggedInUserId = getLoggedInUserId();

  const openInvoicePreview = useCallback(async (row) => {
    const raw = row?._raw || row || {};
    const invNo = String(raw?.invoice_no ?? raw?.invoiceNo ?? "").trim();
    const clientId = raw?.client_id ?? raw?.clientId ?? "";

    if (categoryTab === "business") {
      try {
        let docs = officeDocuments;
        if (clientId) {
          const { data } = await API.post(`document/getAllClientDocuments`, {
            user_id: clientId,
            client_id: clientId,
            document_category: "business",
          });
          docs = Array.isArray(data?.data) ? data.data : [];
        }
        const doc =
          findBusinessOfficeDocument(
            docs,
            raw?.invoice_document_id ?? raw?.invoiceDocumentId ?? invNo,
          ) || findBusinessOfficeDocument(docs, invNo);
        const link =
          getOfficeDocumentLink(doc) ||
          String(
            raw?.invoice_document_link ?? raw?.invoiceDocumentLink ?? "",
          ).trim();
        if (link) {
          setOfficeDocPreview({
            open: true,
            url: link,
            documentName: getOfficeDocumentName(doc) || invNo,
            clientDocumentId:
              doc?.client_document_id ?? doc?.clientDocumentId ?? "",
          });
          return;
        }
        Toaster.warning("Office document not found for this e-filing row.");
      } catch (_e) {
        Toaster.error("Failed to load office document.");
      }
      return;
    }

    let invoiceId =
      raw?.client_invoice_id ??
      raw?.clientInvoiceId ??
      null;

    try {
      if (!invoiceId && clientId && invNo) {
        const { data } = await API.post(`invoice/getAllInvoice`, {
          billed_to: clientId,
        });
        const invoices = Array.isArray(data?.data) ? data.data : [];
        const match = invoices.find((inv) => {
          const nos = [
            inv?.invoice_no,
            inv?.invoiceNo,
            inv?.invoice_number,
          ]
            .filter((x) => x != null && x !== "")
            .map((x) => String(x).trim());
          return nos.includes(invNo);
        });
        if (match) {
          invoiceId = getClientInvoiceId(match);
          if (invoiceId) {
            const response = await INVOICE_ACTION.getInvoiceDetail(invoiceId);
            if (response?.status === 200 && response?.data?.data) {
              setInvoicePreview({
                open: true,
                data: {
                  ...response.data.data,
                  status: response.data.data.status ?? match.status,
                  _invoiceData: match,
                },
              });
              return;
            }
            setInvoicePreview({ open: true, data: match });
            return;
          }
        }
      }

      if (invoiceId) {
        const response = await INVOICE_ACTION.getInvoiceDetail(invoiceId);
        if (response?.status === 200 && response?.data?.data) {
          setInvoicePreview({
            open: true,
            data: response.data.data,
          });
          return;
        }
      }

      Toaster.warning("Invoice details not found for this e-filing row.");
    } catch (_e) {
      Toaster.error("Failed to load invoice.");
    }
  }, [categoryTab, officeDocuments]);

  useEffect(() => {
    const clientId = editRow?.client_id ?? editRow?.clientId;
    if (!editOpen || categoryTab !== "business" || !clientId) {
      if (!editOpen) setOfficeDocuments([]);
      return undefined;
    }
    let cancelled = false;
    dispatch(
      DOCUMENT_ACTION.getAllClientDocuments({
        user_id: clientId,
        client_id: clientId,
        document_category: "business",
      }),
    ).then((action) => {
      if (cancelled) return;
      const list = action?.payload;
      setOfficeDocuments(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, [editOpen, categoryTab, editRow, dispatch]);

  useEffect(() => {
    if (!canAccessEfilingPage()) {
      navigate("/forbidden");
    }
  }, [navigate]);

  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange(dateRange);
      const filters = {
        q: searchQueryRef.current?.trim() || null,
        filing_status: statusFilter !== "All" ? statusFilter : null,
        start_date,
        end_date,
      };
      const rows = await fetchAllClientEfilings(categoryTab, filters);
      setRecords(Array.isArray(rows) ? rows : []);
    } finally {
      setLoading(false);
    }
  }, [categoryTab, dateRange, statusFilter]);

  const loadRef = useRef(load);
  loadRef.current = load;

  // Immediate reload when category / status / date change (same pattern as client-details EfilingSection)
  useEffect(() => {
    load();
  }, [categoryTab, statusFilter, dateRange, load]);

  // Debounce ONLY search — do not depend on `load` or we refetch when category changes;
  // skip first run so we don't duplicate the initial fetch from the effect above.
  const searchInitialSkipRef = useRef(true);
  useEffect(() => {
    if (searchInitialSkipRef.current) {
      searchInitialSkipRef.current = false;
      return;
    }
    const delay = setTimeout(() => {
      loadRef.current();
    }, 2000);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const visibleKeys = useMemo(() => {
    if (categoryTab === "business") {
      return [
        "date",
        "preparer",
        "entity_name",
        "business_type",
        "state_filing",
        "federal_due",
        "fed_payment_method",
        "state_due",
        "state_payment_method",
        "ca_estimate_payment",
        "ca_payment_method",
        "invoice",
        "invoice_amount",
        "invoice_status",
        "filer_comment",
        "signed_consents_link",
        "password",
        "other_comments",
        "filing_status",
      ];
    }
    return [
      "date",
      "preparer",
      "last_name",
      "first_name",
      "fed_due_ref",
      "fed_payment_method",
      "state_due_ref",
      "state_payment_method",
      "filing_status",
      "filer_comment",
      "signed_consents_link",
      "payment_status",
      "other_comments",
      "ack_status",
      "invoice",
    ];
  }, [categoryTab]);

  const tableHeader = useMemo(() => {
    if (categoryTab === "business") {
      return [
        { id: "date", label: "Date" },
        { id: "preparer", label: "Preparer" },
        { id: "entity_name", label: "Entity Name" },
        { id: "business_type", label: "Type" },
        { id: "state_filing", label: "State filing" },
        { id: "federal_due", label: "Federal Due" },
        { id: "fed_payment_method", label: "Fed Payment Method" },
        { id: "state_due", label: "State Due" },
        { id: "state_payment_method", label: "State Payment Method" },
        { id: "ca_estimate_payment", label: "CA estimate payment" },
        { id: "ca_payment_method", label: "CA Payment Method" },
        { id: "invoice", label: "Invoice Number" },
        { id: "invoice_amount", label: "Invoice Amount" },
        { id: "invoice_status", label: "Invoice Status" },
        { id: "filer_comment", label: "Preparer Comments" },
        { id: "signed_consents_link", label: "Signed Consent Link" },
        { id: "password", label: "Password" },
        { id: "other_comments", label: "Filer Comments" },
        { id: "filing_status", label: "Filing Status" },
        { id: "", label: "Action" },
      ];
    }
    return [
      { id: "date", label: "Date" },
      { id: "preparer", label: "Preparer" },
      { id: "first_name", label: "First Name" },
      { id: "last_name", label: "Last Name" },
      { id: "fed_due_ref", label: "Fed Due/Ref" },
      { id: "fed_payment_method", label: "Payment Method" },
      { id: "state_due_ref", label: "State Due/Ref" },
      { id: "state_payment_method", label: "Payment Method" },
      { id: "filing_status", label: "Status" },
      { id: "filer_comment", label: "Preparer Comment" },
      { id: "signed_consents_link", label: "Signed Consents" },
      { id: "payment_status", label: "Payment Status" },
      { id: "other_comments", label: "Filer Comment" },
      { id: "ack_status", label: "Ack. Status" },
      { id: "invoice", label: "INV No. / Amt" },
      { id: "", label: "Action" },
    ];
  }, [categoryTab]);

  const openEdit = useCallback(
    (row) => {
      const raw = row?._raw || row;
      setEditRow(raw || null);
      setEditForm(
        categoryTab === "business"
          ? recordToBusinessEditForm(raw)
          : recordToEditForm(raw),
      );
      setEditFormErrors({});
      setEditOpen(true);
    },
    [categoryTab],
  );

  const openStatusEdit = useCallback((row) => {
    const raw = row?._raw || row;
    const filingStatus = String(raw?.filing_status ?? raw?.status ?? "").trim();
    if (filingStatus === "Rejected" && !canEditStatus) {
      const clientSalesId = raw?.client_sales ?? raw?.clientSales ?? raw?._raw?.client_sales ?? raw?._raw?.clientSales;
      const isClientManager = clientSalesId && String(clientSalesId) === String(loggedInUserId);
      if (!isClientManager) {
        Toaster.error("Only the assigned client manager can edit the status of a rejected e-filing.");
        return;
      }
    }
    setStatusEditRow(raw || null);
    setStatusEditForm({
      filing_status: filingStatus === "Rejected" ? "Pending" : (raw?.filing_status ?? raw?.status ?? "Pending"),
      other_comments: raw?.other_comments ?? raw?.otherComments ?? "",
    });
    setStatusEditFormErrors({});
    setStatusEditOpen(true);
  }, [loggedInUserId, canEditStatus]);

  const editInvoiceDropdown = useMemo(() => {
    const no = editRow?.invoice_no ?? editRow?.invoiceNo ?? "";
    if (categoryTab === "business") {
      return buildBusinessOfficeInvoiceDropdown(officeDocuments, no);
    }
    return [
      { id: "", value: "Select invoice", keepAtTop: true },
      ...(no
        ? [{ id: String(no), value: String(no) }]
        : []),
    ];
  }, [editRow, categoryTab, officeDocuments]);

  const onActionClick = useCallback(
    (action, row) => {
      if (action === "edit") openEdit(row);
      if (action === "editStatus") openStatusEdit(row);
    },
    [openEdit, openStatusEdit],
  );

  const hasAnyClientManagerRejectedRecord = useMemo(() => {
    return (records || []).some((r) => {
      const filingStatus = String(r.filing_status ?? r.status ?? "").trim();
      if (filingStatus !== "Rejected") return false;
      const clientSalesId = r.client_sales ?? r.clientSales ?? r._raw?.client_sales ?? r._raw?.clientSales;
      return clientSalesId && String(clientSalesId) === String(loggedInUserId);
    });
  }, [records, loggedInUserId]);

  const tableActions = useMemo(() => {
    const list = [];
    if (showEditAction) list.push("edit");
    if (canEditStatus || hasAnyClientManagerRejectedRecord) list.push("editStatus");
    return list;
  }, [showEditAction, canEditStatus, hasAnyClientManagerRejectedRecord]);

  const saveEdit = useCallback(async () => {
    const efilingId =
      editRow?.efiling_id ??
      editRow?.business_efiling_id ??
      editRow?.id ??
      editRow?.efilingId;
    if (!efilingId) return;

    const clientFirst =
      editRow?.client_first_name ?? editRow?.first_name ?? "";
    const clientLast = editRow?.client_last_name ?? editRow?.last_name ?? "";

    const validation =
      categoryTab === "business"
        ? validateBusinessEfilingForm(editForm, {
            skipStatusAndFilerComment: true,
          })
        : validateEditEfilingForm(editForm, clientFirst, clientLast, {
            skipStatusAndFilerComment: true,
          });
    if (!validation.valid) {
      setEditFormErrors(validation.errors);
      Toaster.warning("Please fill in all required fields.");
      return;
    }
    setEditFormErrors({});

    setEditSaving(true);
    try {
      const payload =
        categoryTab === "business"
          ? {
              ...buildBusinessEfilingPayload(
                editForm,
                {
                  clientId: editRow?.client_id,
                  preparerEmployeeId:
                    editRow?.preparer_employee_id ??
                    editRow?.preparerEmployeeId,
                  preparerDisplay:
                    editRow?.preparer_display_name ??
                    editRow?.preparer ??
                    editRow?.preparer_name,
                  resolvedInvoice: null,
                  originalRecord: editRow,
                  officeDocument: findBusinessOfficeDocument(
                    officeDocuments,
                    editForm.invoice_no,
                  ),
                  invoiceTotalAmount: () =>
                    editForm.invoice_amount_readonly
                      ? parseFloat(
                          String(editForm.invoice_amount_readonly).replace(
                            /[$,\s]/g,
                            "",
                          ),
                        ) || null
                      : null,
                },
                { preserveStatusAndFilerComment: true },
              ),
              category: "business",
            }
          : buildEfilingUpdatePayload(
              editForm,
              {
                clientId: editRow?.client_id,
                category: categoryTab,
                clientFirst,
                clientLast,
                preparerEmployeeId:
                  editRow?.preparer_employee_id ?? editRow?.preparerEmployeeId,
                preparerDisplay:
                  editRow?.preparer_display_name ??
                  editRow?.preparer ??
                  editRow?.preparer_name,
                resolvedInvoice: null,
                originalRecord: editRow,
                invoiceTotalAmount: () =>
                  editForm.invoice_amount_readonly
                    ? parseFloat(
                        String(editForm.invoice_amount_readonly).replace(
                          /[$,\s]/g,
                          "",
                        ),
                      ) || null
                    : null,
              },
              { preserveStatusAndFilerComment: true },
            );

      const res = await updateClientEfiling(efilingId, payload);
      const body = res?.data || {};
      if (res?.status === 200 && body.status === 1) {
        await load();
        setEditOpen(false);
        Toaster.success(body.message || "E-filing updated.");
      } else {
        Toaster.error(
          body.message || body.error || "Could not update e-filing.",
        );
      }
    } catch (e) {
      Toaster.error(
        e?.response?.data?.message ||
          e?.message ||
          "Could not update e-filing.",
      );
    } finally {
      setEditSaving(false);
    }
  }, [editForm, editRow, categoryTab, load]);

  const saveStatusEdit = useCallback(async () => {
    const efilingId =
      statusEditRow?.efiling_id ??
      statusEditRow?.business_efiling_id ??
      statusEditRow?.id ??
      statusEditRow?.efilingId;
    if (!efilingId) return;

    const validation = validateEfilingStatusForm(statusEditForm);
    if (!validation.valid) {
      setStatusEditFormErrors(validation.errors);
      Toaster.warning("Please fill in all required fields.");
      return;
    }
    setStatusEditFormErrors({});

    setStatusEditSaving(true);
    try {
      const payload = {
        ...buildEfilingStatusUpdatePayload(statusEditForm),
        ...(categoryTab === "business" ? { category: "business" } : {}),
      };
      const res = await updateClientEfiling(efilingId, payload);
      const body = res?.data || {};
      if (res?.status === 200 && body.status === 1) {
        await load();
        setStatusEditOpen(false);
        Toaster.success(body.message || "Status updated.");
      } else {
        Toaster.error(
          body.message || body.error || "Could not update status.",
        );
      }
    } catch (e) {
      Toaster.error(
        e?.response?.data?.message ||
          e?.message ||
          "Could not update status.",
      );
    } finally {
      setStatusEditSaving(false);
    }
  }, [statusEditForm, statusEditRow, load, categoryTab]);

  const tableData = useMemo(() => {
    const isBiz = categoryTab === "business";
    const sorted = [...(records || [])].sort((a, b) => {
      const pick = (r) => {
        const d =
          r.filing_date ||
          r.date ||
          r.created_at ||
          r.createdAt ||
          r.updated_at ||
          "";
        const t = new Date(d).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      return pick(a) - pick(b);
    });
    return sorted.map((r, i) => {
      const d =
        r.filing_date ||
        r.date ||
        r.created_at ||
        r.createdAt ||
        r.updated_at ||
        "";
      const dateLabel = d
        ? moment(d).isValid()
          ? moment(d).format("MM/DD/YYYY")
          : String(d)
        : "";

      const invoiceCell = (() => {
        const invNo = r.invoice_no ?? r.invoiceNo ?? "";
        const invAmt =
          r.invoice_amount != null
            ? formatMoneyDisplay(r.invoice_amount)
            : r.invoiceAmount != null
              ? formatMoneyDisplay(r.invoiceAmount)
              : "";
        const text = isBiz
          ? invNo
            ? String(invNo)
            : ""
          : invNo && invAmt
            ? `${invNo} - $${invAmt}`
            : invNo
              ? String(invNo)
              : invAmt
                ? `$${invAmt}`
                : "";
        if (!text) return <span className="efiling-nowrap">—</span>;
        return (
          <button
            type="button"
            className="efiling-invoice-link efiling-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              openInvoicePreview(r);
            }}
          >
            {text}
          </button>
        );
      })();

      const enabledActions = {
        ...(showEditAction ? { edit: true } : {}),
        editStatus: (() => {
          if (canEditStatus) return true;
          const filingStatus = String(r.filing_status ?? r.status ?? "").trim();
          if (filingStatus === "Rejected") {
            const clientSalesId =
              r.client_sales ??
              r.clientSales ??
              r._raw?.client_sales ??
              r._raw?.clientSales;
            return Boolean(
              clientSalesId && String(clientSalesId) === String(loggedInUserId),
            );
          }
          return false;
        })(),
      };

      const meta = {
        _efiling_id:
          r.efiling_id ??
          r.business_efiling_id ??
          r.id ??
          r.efilingId ??
          null,
        _raw: r,
        key:
          r.efiling_id ??
          r.business_efiling_id ??
          r.id ??
          `ef-${i}`,
        enabledActions,
      };

      // Table renders by Object.keys order — must match header column order.
      if (isBiz) {
        return {
          date: dateLabel,
          preparer:
            r.preparer_display_name || r.preparer || r.preparer_name || "---",
          entity_name: r.entity_name ?? r.entityName ?? "",
          business_type: r.business_type ?? r.businessType ?? r.type ?? "",
          state_filing: r.state_filing ?? r.stateFiling ?? "",
          federal_due: r.federal_due ?? r.federalDue ?? "",
          fed_payment_method:
            r.fed_payment_method ?? r.fedPaymentMethod ?? "",
          state_due: r.state_due ?? r.stateDue ?? "",
          state_payment_method:
            r.state_payment_method ?? r.statePaymentMethod ?? "",
          ca_estimate_payment:
            r.ca_estimate_payment ?? r.caEstimatePayment ?? "",
          ca_payment_method: r.ca_payment_method ?? r.caPaymentMethod ?? "",
          invoice: invoiceCell,
          invoice_amount: (() => {
            const amt =
              r.invoice_amount != null
                ? formatMoneyDisplay(r.invoice_amount)
                : r.invoiceAmount != null
                  ? formatMoneyDisplay(r.invoiceAmount)
                  : "";
            return amt ? `$${amt}` : "";
          })(),
          invoice_status:
            r.invoice_status ??
            r.invoiceStatus ??
            r.payment_status ??
            r.paymentStatus ??
            "",
          filer_comment: r.filer_comment ?? r.filerComment ?? "",
          signed_consents_link: (
            <EfilingSignedConsentsDisplay
              value={r.signed_consents_link ?? r.signedConsentsLink ?? ""}
            />
          ),
          password: r.password ?? r.consent_password ?? "",
          other_comments: r.other_comments ?? r.otherComments ?? "",
          filing_status: renderEfilingStatusBadge(
            r.filing_status ?? r.status ?? "",
          ),
          ...meta,
        };
      }

      return {
        date: dateLabel,
        preparer:
          r.preparer_display_name || r.preparer || r.preparer_name || "---",
        first_name: r.client_first_name ?? r.first_name ?? "",
        last_name: r.client_last_name ?? r.last_name ?? "",
        fed_due_ref: r.fed_due_ref ?? r.fedDueRef ?? "",
        fed_payment_method: r.fed_payment_method ?? r.fedPaymentMethod ?? "",
        state_due_ref: r.state_due_ref ?? r.stateDueRef ?? "",
        state_payment_method:
          r.state_payment_method ?? r.statePaymentMethod ?? "",
        filing_status: renderEfilingStatusBadge(
          r.filing_status ?? r.status ?? "",
        ),
        filer_comment: r.filer_comment ?? r.filerComment ?? "",
        signed_consents_link: (
          <EfilingSignedConsentsDisplay
            value={r.signed_consents_link ?? r.signedConsentsLink ?? ""}
          />
        ),
        payment_status: r.payment_status ?? r.paymentStatus ?? "",
        other_comments: r.other_comments ?? r.otherComments ?? "",
        ack_status: r.ack_status ?? r.ackStatus ?? "",
        invoice: invoiceCell,
        ...meta,
      };
    });
  }, [
    records,
    showEditAction,
    canEditStatus,
    openInvoicePreview,
    loggedInUserId,
    categoryTab,
  ]);

  const fields = useMemo(() => [], []);

  return (
    <div className="efiling-admin-page">
      <div className="efiling-admin-toolbar">
        <div className="efiling-admin-toolbar-left">
          <div className="efiling-admin-type-tabs">
            <button
              type="button"
              className={`efiling-admin-type-tab ${categoryTab === "individual" ? "is-active" : ""}`}
              onClick={() => setCategoryTab("individual")}
              disabled={loading || editSaving}
            >
              Individual
            </button>
            <button
              type="button"
              className={`efiling-admin-type-tab ${categoryTab === "business" ? "is-active" : ""}`}
              onClick={() => setCategoryTab("business")}
              disabled={loading || editSaving}
            >
              Business
            </button>
          </div>
        </div>
      </div>

      <div
        style={{ marginTop: "20px" }}
        className="client-details-invoices panel"
      >
        <div className="efiling-admin-filters-row">
          <div className="efiling-admin-search">
            <SearchIcon className="efiling-admin-search-icon" />
            <input
              type="text"
              placeholder={
                categoryTab === "business"
                  ? "Search Preparer / Entity Name"
                  : "Search Preparer / First name / Last name"
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  load();
                }
              }}
              disabled={editSaving}
            />
          </div>

          <div className="efiling-admin-filters-right">
            <div className="efiling-admin-filter">
              <Labelbox
                type="select"
                labelname="Date Range"
                dropdown={DATE_RANGES}
                value={dateRange}
                changeData={(v) => setDateRange(v)}
                disabled={loading || editSaving || statusEditSaving}
                error={false}
                errmsg={null}
              />
            </div>
          </div>
        </div>

        <div className="efiling-admin-status-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`efiling-admin-status-tab ${statusFilter === tab.id ? "is-active" : ""}`}
              onClick={() => setStatusFilter(tab.id)}
              disabled={loading || editSaving || statusEditSaving}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="efiling-admin-loading">Loading…</div>
        ) : (
          <Table
            title=""
            header={tableHeader}
            data={tableData}
            EnableSno={false}
            actions={tableActions}
            onActionClick={onActionClick}
            fields={fields}
            restrictActionIcon={tableActions.length > 0}
            hiddenFields={["key", "_efiling_id", "_raw"]}
            aligncss="dashboard-table"
          />
        )}
      </div>

      <EfilingEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        saving={editSaving}
        hideStatusAndFilerComment
        form={editForm}
        setForm={setEditForm}
        formErrors={editFormErrors}
        onClearError={(key) =>
          setEditFormErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          })
        }
        clientFirst={editRow?.client_first_name ?? editRow?.first_name ?? ""}
        clientLast={editRow?.client_last_name ?? editRow?.last_name ?? ""}
        categoryTab={categoryTab}
        invoiceDropdown={editInvoiceDropdown}
        onSave={saveEdit}
        clientId={
          editRow?.client_id ??
          editRow?.clientId ??
          editRow?._raw?.client_id ??
          ""
        }
      />

      <EfilingStatusEditModal
        open={statusEditOpen}
        onClose={() => setStatusEditOpen(false)}
        saving={statusEditSaving}
        form={statusEditForm}
        setForm={setStatusEditForm}
        formErrors={statusEditFormErrors}
        onClearError={(key) =>
          setStatusEditFormErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          })
        }
        onSave={saveStatusEdit}
        isRejectedStatus={
          statusEditRow?.filing_status === "Rejected" ||
          statusEditRow?.status === "Rejected"
        }
        isAdmin={canEditStatus}
      />

      <EfilingDocumentPreviewModal
        open={officeDocPreview.open}
        sourceUrl={officeDocPreview.url}
        documentName={officeDocPreview.documentName}
        clientDocumentId={officeDocPreview.clientDocumentId}
        title="Invoice Preview"
        onClose={() =>
          setOfficeDocPreview({
            open: false,
            url: "",
            documentName: "",
            clientDocumentId: "",
          })
        }
      />

      {invoicePreview.open && invoicePreview.data && (
        <Modal
          className="timesheet-view-modal"
          open={invoicePreview.open}
          maxWidth
          handleClose={() => setInvoicePreview({ open: false, data: null })}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontWeight: "bold" }}>Invoice Preview</h2>
            <button
              type="button"
              className="close-btn"
              onClick={() => setInvoicePreview({ open: false, data: null })}
            >
              ✕
            </button>
          </div>
          <ContractInvoiceView
            id={false}
            data={invoicePreview.data}
            label="Invoice"
          />
        </Modal>
      )}
    </div>
  );
}
